import { ContentService } from '@common/services/content.service';
import { Injectable, Inject, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isNullOrUndefined } from '@common/util';
import { of } from 'rxjs';
import {
    switchMap,
    map,
    distinctUntilChanged,
    catchError,
    tap,
} from 'rxjs/operators';
import {
    CMS_ALLOW_LINKS,
    CMS_LOCALE,
    CMS_PRODUCT,
    CMS_REGION,
    CMS_URL,
} from '@common/app/app.config';
import { CommunityService } from '@common/services/community.service';
import { LanguageService } from '@common/services/language.service';
import { ContentFallbackServiceBase } from '@common/services/content-fallback.service.base';
import { Community } from '@common/model/community';
import { Content } from '@common/model/content';

@Injectable()
export class EnJpContentService extends ContentService {
    constructor(
        protected http: HttpClient,
        @Inject(CMS_URL) baseUrl: string,
        @Inject(CMS_PRODUCT) product: string,
        @Inject(CMS_REGION) region: string,
        @Inject(CMS_LOCALE) defaultLocale: string,
        @Inject(CMS_ALLOW_LINKS) cmsAllowLinks: boolean,
        protected communityService: CommunityService,
        @Optional() languageService: LanguageService,
        @Optional() fallbackService: ContentFallbackServiceBase,
    ) {
        super(
            http,
            baseUrl,
            product,
            region,
            defaultLocale,
            cmsAllowLinks,
            communityService,
            languageService,
            fallbackService,
        );
    }

    load(auctionCode?: string, noAuction = false): void {
        this.loadByCommunityCode(null, auctionCode, noAuction);
    }

    loadByCommunityCode(
        communityCode?: string,
        auctionCode?: string,
        noAuction = false,
    ): void {
        this.communityService.community$
            .pipe(
                catchError(() => of(new Community())),
                tap(() => this.loading$.next(true)),
                map((community) =>
                    this.bakeUrl(
                        community,
                        communityCode,
                        auctionCode,
                        noAuction,
                    ),
                ),
                distinctUntilChanged(),
                switchMap((url) =>
                    this.http.get<Content[]>(url).pipe(catchError(() => [])),
                ),
            )
            .subscribe((content) => this.content.next(content));
    }

    private bakeUrl(
        community: Community,
        communityCode: string,
        auctionCode: string,
        noAuction: boolean,
    ): string {
        let url = this.url;

        if (this.languageService) {
            url = url + '&locale=' + this.languageService.currentLanguageCode;
        } else {
            url = url + '&locale=' + this.defaultLocale;
        }

        if (auctionCode && noAuction === false) {
            url += `&auction=${auctionCode}`;
        } else if (
            community &&
            community.targetAuction &&
            noAuction === false
        ) {
            url += `&auction=${community.targetAuction.code}`;
        }
        if (
            isNullOrUndefined(communityCode) &&
            !isNullOrUndefined(community.code)
        ) {
            url += '&community=' + community.code;
        }
        // Param communityCode will overwrite code from real community
        if (!isNullOrUndefined(communityCode)) {
            url += '&community=' + communityCode;
        }
        if (this.showPreview) {
            url += '&preview=true';
        }

        return url;
    }
}
